import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { ChevronLeft, AlertTriangle, CheckCircle2 } from "lucide-react";
import DeliverPlayerRow from "../../components/club-network/DeliverPlayerRow";
import { playerMetaParts } from "../../components/club-network/playerRecord";
import { trustedAgentService } from "../../services/clubNetworkApi";

// B3 / B3b — the commissioned agent's deliver flow.
//
//   • "pick"    (B3):  the ACCEPTED players (record data), a checkbox + optional
//                      note per player, the foot-rule, "Deliver with no players"
//                      and "Review N proposals".
//   • "summary" (B3b): the summary note, the picked players, the "delivering ends
//                      the search / 5-day debrief timer" warning, Back / Deliver.
//   • "empty"   (A3):  "An honest end is a result" + a REQUIRED reason →
//                      "Send, nothing found".
//
// Player data is the hydrated record only (Annex Z.7); the server re-validates
// every ticked player against the accepted set, so the tick is the contract —
// "only what you tick reaches the club".

const DeliverSearchRequest = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const clubName = location.state?.clubName || "the club";

  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [step, setStep] = useState("pick"); // pick | summary | empty
  const [checked, setChecked] = useState(() => new Set());
  const [notes, setNotes] = useState({});
  const [summaryNote, setSummaryNote] = useState("");
  const [emptyReason, setEmptyReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [done, setDone] = useState(null); // { empty: bool, count: n }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    trustedAgentService.getAcceptedPlayers(id).then((res) => {
      if (cancelled) return;
      if (res.success) setPlayers(res.data || []);
      else setError(res.error || "Could not load your accepted players.");
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const toggle = (playerId) =>
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(playerId)) next.delete(playerId);
      else next.add(playerId);
      return next;
    });

  const setNote = (playerId, value) =>
    setNotes((prev) => ({ ...prev, [playerId]: value }));

  const pickedPlayers = useMemo(
    () => players.filter((p) => checked.has(p.id)),
    [players, checked],
  );
  const pickedCount = pickedPlayers.length;

  const buildProposals = () =>
    pickedPlayers.map((p) => {
      const note = (notes[p.id] || "").trim();
      return note ? { player_id: p.id, note } : { player_id: p.id };
    });

  const submit = async ({ empty }) => {
    setSubmitting(true);
    setSubmitError(null);
    const res = await trustedAgentService.deliver(id, {
      proposals: empty ? [] : buildProposals(),
      summaryNote: empty ? "" : summaryNote.trim(),
      emptyReason: empty ? emptyReason.trim() : "",
    });
    setSubmitting(false);
    if (res.success) setDone({ empty, count: empty ? 0 : pickedCount });
    else setSubmitError(res.error || "Delivery failed. Please try again.");
  };

  // ── Success ────────────────────────────────────────────────────────────────
  if (done) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-xl mx-auto px-4 sm:px-6 py-16 flex flex-col items-center text-center gap-4">
          <CheckCircle2 className="w-12 h-12 text-[#16A34A]" />
          <h1 className="text-[20px] font-bold text-[#111827]">
            {done.empty
              ? "Sent — you closed this search honestly"
              : `Delivered ${done.count} proposal${done.count === 1 ? "" : "s"}`}
          </h1>
          <p className="text-[13.5px] text-gray-500 leading-relaxed">
            The search is now closed. Your chat with {clubName} switches to a
            5-day timer for the debrief — the club can extend it.
          </p>
          <button
            onClick={() => navigate("/network/search-requests/inbox")}
            className="mt-2 bg-[#2283FF] text-white rounded-lg px-5 py-2.5 text-[13.5px] font-semibold hover:bg-[#1b6fd8]"
          >
            Back to my requests
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return <div className="flex justify-center py-20 text-gray-500">Loading…</div>;
  }
  if (error) {
    return (
      <div className="flex flex-col items-center py-20 text-center gap-3">
        <p className="text-red-500">{error}</p>
        <button
          onClick={() => navigate("/network/search-requests/inbox")}
          className="px-4 py-2 border rounded-lg hover:bg-gray-50"
        >
          Back to my requests
        </button>
      </div>
    );
  }

  const backLink = () => navigate("/network/search-requests/inbox");

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 pb-28">
        <button
          onClick={step === "pick" ? backLink : () => setStep("pick")}
          className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-[#2283FF] mb-4"
        >
          <ChevronLeft className="w-4 h-4" />
          {step === "pick" ? "Back to my requests" : "Back"}
        </button>

        <h1 className="text-[22px] font-bold text-[#111827] mb-1">
          {`Deliver to ${clubName}`}
        </h1>

        {/* ── STEP: pick (B3) ───────────────────────────────────────────────── */}
        {step === "pick" && (
          <>
            <p className="text-[13px] text-gray-500 mb-5">
              These are the players you accepted on this commission. Tick the ones
              you want the club to see.
            </p>

            {players.length === 0 ? (
              <div className="bg-white border border-[#D6DBE3] rounded-xl p-6 text-center text-[13.5px] text-gray-500">
                You have no accepted players on this commission yet. You can still
                deliver an honest empty result below.
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {players.map((p) => (
                  <DeliverPlayerRow
                    key={p.id}
                    player={p}
                    checked={checked.has(p.id)}
                    note={notes[p.id]}
                    onToggle={toggle}
                    onNoteChange={setNote}
                  />
                ))}
              </div>
            )}

            {/* Foot-rule */}
            <p className="text-[12px] text-gray-400 leading-relaxed mt-4 bg-[#F8FAFC] border border-[#E5E7EB] rounded-lg px-3.5 py-3">
              Accepted players are your working material, only what you tick here
              reaches the club.
            </p>
          </>
        )}

        {/* ── STEP: summary (B3b) ───────────────────────────────────────────── */}
        {step === "summary" && (
          <>
            <p className="text-[13px] text-gray-500 mb-5">
              Review before you send. This is exactly what the club will see.
            </p>

            <label className="block mb-5">
              <span className="text-[12px] font-semibold text-[#374151]">
                A note to the club (optional)
              </span>
              <span className="block text-[11.5px] text-gray-400 mb-1.5">
                Travels with the delivery, sits above the proposals and in the email.
              </span>
              <textarea
                value={summaryNote}
                onChange={(e) => setSummaryNote(e.target.value)}
                rows={3}
                placeholder="Anything the club should read first…"
                className="w-full text-[13px] border border-[#D6DBE3] rounded-lg px-3 py-2 resize-y focus:outline-none focus:ring-2 focus:ring-[#2283FF]/30 focus:border-[#2283FF]"
              />
            </label>

            <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
              {pickedCount === 1
                ? "1 player in this delivery"
                : `${pickedCount} players in this delivery`}
            </span>
            <div className="flex flex-col gap-2.5 mt-2 mb-5">
              {pickedPlayers.map((p) => {
                const meta = playerMetaParts(p);
                const note = (notes[p.id] || "").trim();
                return (
                  <div
                    key={p.id}
                    className="bg-white border border-[#E5E7EB] rounded-xl p-3.5"
                  >
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="text-[14px] font-bold text-[#111827]">
                        {p.name}
                      </span>
                      {meta.length > 0 && (
                        <span className="text-[12px] text-gray-500">
                          {meta.join(" · ")}
                        </span>
                      )}
                    </div>
                    {note ? (
                      <p className="text-[12.5px] text-[#374151] mt-1.5 break-words">
                        {note}
                      </p>
                    ) : (
                      <p className="text-[12px] italic text-gray-400 mt-1">
                        No note
                      </p>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="flex items-start gap-2.5 bg-[#FFFBEB] border border-[#FDE68A] rounded-lg px-3.5 py-3">
              <AlertTriangle className="w-4 h-4 text-[#B45309] flex-none mt-0.5" />
              <p className="text-[12.5px] text-[#92400E] leading-relaxed">
                Delivering ends the search. Your chat with the club switches to a
                5-day timer for the debrief — the club can extend it.
              </p>
            </div>
          </>
        )}

        {/* ── STEP: empty path (A3) ─────────────────────────────────────────── */}
        {step === "empty" && (
          <>
            <div className="bg-white border border-[#D6DBE3] rounded-xl p-5 sm:p-6 mt-4">
              <h2 className="text-[16px] font-bold text-[#111827]">
                An honest end is a result
              </h2>
              <p className="text-[13px] text-gray-500 leading-relaxed mt-1.5 mb-4">
                No players to send is a valid outcome. Tell the club what you
                found, so they can decide the next step. This ends the search.
              </p>
              <label className="block">
                <span className="text-[12px] font-semibold text-[#374151]">
                  Your reason (required)
                </span>
                <textarea
                  value={emptyReason}
                  onChange={(e) => setEmptyReason(e.target.value)}
                  rows={4}
                  placeholder="What you worked, and why nothing fit the brief…"
                  className="mt-1.5 w-full text-[13px] border border-[#D6DBE3] rounded-lg px-3 py-2 resize-y focus:outline-none focus:ring-2 focus:ring-[#2283FF]/30 focus:border-[#2283FF]"
                />
              </label>
            </div>
            <div className="flex items-start gap-2.5 bg-[#FFFBEB] border border-[#FDE68A] rounded-lg px-3.5 py-3 mt-4">
              <AlertTriangle className="w-4 h-4 text-[#B45309] flex-none mt-0.5" />
              <p className="text-[12.5px] text-[#92400E] leading-relaxed">
                Delivering ends the search. Your chat with the club switches to a
                5-day timer for the debrief — the club can extend it.
              </p>
            </div>
          </>
        )}

        {submitError && (
          <p className="text-[13px] text-red-500 mt-4">{submitError}</p>
        )}
      </div>

      {/* Sticky footer actions (M·B3: the empty path lives in the footer) */}
      <div className="fixed bottom-0 inset-x-0 bg-white border-t border-[#E5E7EB] px-4 sm:px-6 py-3">
        <div className="max-w-2xl mx-auto flex flex-col sm:flex-row gap-2.5 sm:justify-end">
          {step === "pick" && (
            <>
              <button
                type="button"
                onClick={() => setStep("empty")}
                className="order-2 sm:order-1 border border-gray-200 rounded-lg px-4 py-2.5 text-[13.5px] font-semibold text-[#374151] hover:bg-gray-50"
              >
                Deliver with no players
              </button>
              <button
                type="button"
                disabled={pickedCount === 0}
                onClick={() => setStep("summary")}
                className="order-1 sm:order-2 bg-[#2283FF] text-white rounded-lg px-5 py-2.5 text-[13.5px] font-semibold hover:bg-[#1b6fd8] disabled:opacity-50"
              >
                {pickedCount === 1
                  ? "Review 1 proposal"
                  : `Review ${pickedCount} proposals`}
              </button>
            </>
          )}
          {step === "summary" && (
            <>
              <button
                type="button"
                onClick={() => setStep("pick")}
                className="order-2 sm:order-1 border border-gray-200 rounded-lg px-4 py-2.5 text-[13.5px] font-semibold text-[#374151] hover:bg-gray-50"
              >
                Back
              </button>
              <button
                type="button"
                disabled={submitting}
                onClick={() => submit({ empty: false })}
                className="order-1 sm:order-2 bg-[#2283FF] text-white rounded-lg px-5 py-2.5 text-[13.5px] font-semibold hover:bg-[#1b6fd8] disabled:opacity-60"
              >
                {submitting ? "Delivering…" : "Deliver"}
              </button>
            </>
          )}
          {step === "empty" && (
            <button
              type="button"
              disabled={submitting || !emptyReason.trim()}
              onClick={() => submit({ empty: true })}
              className="bg-[#2283FF] text-white rounded-lg px-5 py-2.5 text-[13.5px] font-semibold hover:bg-[#1b6fd8] disabled:opacity-50"
            >
              {submitting ? "Sending…" : "Send, nothing found"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeliverSearchRequest;
